// src/pages/settings/forms/builderDrawer/BuilderCanvas.tsx
import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Input, Checkbox, Select, Typography, Button } from "antd";
import { InboxOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";

import { FormElement } from "../../../../../api/formApi";
import { normaliseFieldType, patchForTypeChange } from "../../../../../components/forms/fieldTypes";
import { typeSelectOptions } from "./formElementTypes";
import { SortableItem } from "./elements/SortableItem";

const { Text } = Typography;

interface BuilderCanvasProps {
	formFields: FormElement[];
	sectionId?: string | number;
	onUpdateField: (sectionId: string | number | undefined, fieldId: string | number, newValues: Partial<FormElement>) => void;
	onDeleteField: (sectionId: string | number | undefined, fieldId: string | number) => void;
	onReorderFields: (sectionId: string | number | undefined, newOrder: FormElement[]) => void;
	highlight?: boolean;
}

export const BuilderCanvas: React.FC<BuilderCanvasProps> = ({ formFields, sectionId, onUpdateField, onDeleteField, onReorderFields, highlight = false }) => {
	const { isOver, setNodeRef } = useDroppable({
		id: "builder-canvas",
	});

	const canvasStyle: React.CSSProperties = {
		border: isOver || highlight ? "2px dashed var(--mg-primary)" : "1px dashed var(--mg-border)",
		minHeight: "400px",
		padding: 12,
		transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
		borderRadius: 6,
		backgroundColor: isOver || highlight ? "var(--mg-primary-bg)" : "transparent",
	};

	const handleTypeChange = (field: FormElement, nextType: string) => {
		onUpdateField(sectionId, field.id, patchForTypeChange(field, nextType) as Partial<FormElement>);
	};

	// One input per option — a single comma-joined box cannot round-trip a comma or a
	// trailing space, so typing either one was silently dropped.
	const renderOptionsEditor = (field: FormElement, title = "Options", addLabel = "Add Option") => (
		<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Text style={{ fontSize: 11 }}>{title}</Text>
			</div>
			{(field.options || []).map((opt, idx) => (
				<div key={idx} style={{ display: "flex", gap: 6, alignItems: "center" }}>
					<Input
						value={opt}
						onChange={(e) => {
							const next = [...(field.options || [])];
							next[idx] = e.target.value;
							onUpdateField(sectionId, field.id, { options: next });
						}}
						placeholder={`Option ${idx + 1}`}
						size="small"
						style={{ flex: 1 }}
					/>
					<Button
						type="text"
						danger
						size="small"
						icon={<CloseOutlined />}
						onClick={() => {
							const next = (field.options || []).filter((_, i) => i !== idx);
							onUpdateField(sectionId, field.id, { options: next });
						}}
						style={{ flexShrink: 0, width: 24, height: 24, minWidth: 24, padding: 0 }}
					/>
				</div>
			))}
			<Button
				type="dashed"
				size="small"
				icon={<PlusOutlined />}
				onClick={() => onUpdateField(sectionId, field.id, { options: [...(field.options || []), ""] })}
				style={{ marginTop: 2 }}
			>
				{addLabel}
			</Button>
		</div>
	);

	// Type + question apply to every field, so they live in one shared header rather
	// than being duplicated across the per-type panels below.
	const renderTypeAndTitle = (field: FormElement) => {
		const canonical = normaliseFieldType(field.type);
		// heading / paragraph carry their body text in `placeholder`, not `question`.
		const isBlock = canonical === "heading" || canonical === "paragraph";
		const textLabel = canonical === "heading" ? "Heading text" : canonical === "paragraph" ? "Paragraph text" : "Question";
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<div>
					<Text style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Field type</Text>
					<Select
						value={canonical}
						onChange={(next) => handleTypeChange(field, next)}
						options={typeSelectOptions}
						size="small"
						style={{ width: "100%" }}
					/>
					<Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2 }}>
						Changing the type keeps the question and options. Answers already
						submitted for this field keep their original shape.
					</Text>
				</div>
				<div>
					<Text style={{ fontSize: 11, display: "block", marginBottom: 4 }}>{textLabel}</Text>
					{/* The RAW stored string — any derive-on-render transform here eats keystrokes. */}
					<Input
						value={isBlock ? (field.placeholder ?? "") : (field.question ?? "")}
						onChange={(e) => onUpdateField(sectionId, field.id, isBlock ? { placeholder: e.target.value } : { question: e.target.value })}
						placeholder={isBlock ? "Enter text" : "Enter question"}
						size="small"
					/>
					{!isBlock && (
						<Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2 }}>
							This is what people filling the form see.
						</Text>
					)}
				</div>
			</div>
		);
	};

	const renderTextSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div>
				<Text style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Placeholder</Text>
				<Input value={field.placeholder || ""} onChange={(e) => onUpdateField(sectionId, field.id, { placeholder: e.target.value })} placeholder="Enter placeholder" size="small" />
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderSingleSelectSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{renderOptionsEditor(field)}
			<Text type="secondary" style={{ fontSize: 10 }}>
				Shown as radio buttons; only one option can be selected.
			</Text>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderMultiSelectSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{renderOptionsEditor(field)}
			<Text type="secondary" style={{ fontSize: 10 }}>
				Shown as checkboxes; multiple options can be selected.
			</Text>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderMultiselectSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{renderOptionsEditor(field)}
			<Text type="secondary" style={{ fontSize: 10 }}>
				Shown as a dropdown; multiple options can be selected.
			</Text>
			<div>
				<Text style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Placeholder</Text>
				<Input value={field.placeholder || ""} onChange={(e) => onUpdateField(sectionId, field.id, { placeholder: e.target.value })} placeholder="Select options" size="small" />
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderSelectSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{renderOptionsEditor(field)}
			<div>
				<Text style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Placeholder</Text>
				<Input value={field.placeholder || ""} onChange={(e) => onUpdateField(sectionId, field.id, { placeholder: e.target.value })} placeholder="Select placeholder" size="small" />
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderDateSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div>
				<Text style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Placeholder</Text>
				<Input value={field.placeholder || ""} onChange={(e) => onUpdateField(sectionId, field.id, { placeholder: e.target.value })} placeholder="Enter placeholder" size="small" />
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderFileSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{renderOptionsEditor(field, "Allowed File Types", "Add Type")}
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderSignatureSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Checkbox checked={field.required || false} onChange={(e) => onUpdateField(sectionId, field.id, { required: e.target.checked })} />
				<Text style={{ fontSize: 12 }}>Required</Text>
			</div>
		</div>
	);

	const renderFieldSettings = (field: FormElement) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{renderTypeAndTitle(field)}
			{renderTypeSpecificSettings(field)}
		</div>
	);

	const renderTypeSpecificSettings = (field: FormElement) => {
		switch (normaliseFieldType(field.type)) {
			case "text":
			case "email":
			case "phone":
			case "number":
			case "textarea":
				return renderTextSettings(field);
			case "multi_select":
				return renderMultiSelectSettings(field);
			case "single_select":
				return renderSingleSelectSettings(field);
			case "select":
				return renderSelectSettings(field);
			case "multiselect":
				return renderMultiselectSettings(field);
			case "date":
			case "time":
			case "datetime":
				return renderDateSettings(field);
			case "file":
				return renderFileSettings(field);
			case "signature":
				return renderSignatureSettings(field);
			case "heading":
			case "paragraph":
				return null;
			default:
				return <Text style={{ fontSize: 11 }}>No settings for "{field.type}"</Text>;
		}
	};

	return (
		<SortableContext items={formFields} strategy={verticalListSortingStrategy}>
			<div ref={setNodeRef} style={canvasStyle}>
				{formFields.length === 0 ? (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							textAlign: "center",
							padding: 32,
							flexDirection: "column",
						}}
					>
						<InboxOutlined style={{ fontSize: 48, marginBottom: 12, color: "var(--mg-text-disabled)" }} />
						<Text style={{ fontSize: 13, color: "var(--mg-text-secondary)" }}>
							Drag elements from the panel to start building
						</Text>
					</div>
				) : (
					formFields.map((field) => (
						<SortableItem key={field.id} field={field} onDeleteField={(fieldId) => onDeleteField(sectionId, fieldId)}>
							{renderFieldSettings(field)}
						</SortableItem>
					))
				)}
			</div>
		</SortableContext>
	);
};
